import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useApi,
  configApiRef,
  identityApiRef,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import {
  Progress,
  Page,
  Header,
  Content,
  InfoCard,
  Table,
} from '@backstage/core-components';
import {
  Box,
  Button,
  Typography,
  TextField,
  MenuItem,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import Link from '@material-ui/core/Link';
import {
  checkBackendHealth,
  fetchAllureReportSummary,
  buildProjectName,
  parseReportIdFromUrl,
} from '../services/allure';
import { AllureReportLink } from './AllureReportLink';
import {
  BUSINESS_UNITS,
  ENVIRONMENTS,
  WEBSITES,
  getTagsForWebsite,
  getBusinessUnitById,
  getUrl,
  type Environment,
  getAvailableFeatures,
  getFeatureValue,
  type Website,
  type FeatureModule,
  type TagOption,
  PROD_ODOO_FEATURE,
} from '../constants';

// GitHub Actions workflow status colors
const GITHUB_WORKFLOW_COLORS = {
  success: '#28a745',
  failure: '#cb2431',
  cancelled: '#6a737d',
  in_progress: '#0366d6',
  queued: '#6a737d',
  skipped: '#6a737d',
} as const;

interface WorkflowInputs {
  env: Environment;
  build_image: boolean;
  country: string;
  business_unit: string;
  website: Website;
  feature: string;
  allure_tag: string;
  branch: string;
}

interface TriggerResult {
  success: boolean;
  message?: string;
  workflow?: string;
  repository?: string;
  inputs?: Record<string, string>;
  error?: string;
  details?: string;
}

interface WorkflowRun {
  id: number;
  run_number: number;
  name: string;
  status: string;
  conclusion: string | null;
  html_url: string;
  created_at: string;
  updated_at: string;
  head_branch: string;
  event: string;
  actor: {
    login: string;
    avatar_url: string;
  };
  backstage_user?: string | null;
  trigger_source?: string;
  report_url?: string | null;
  cached_metadata?: {
    backstage_user: string;
    triggered_at: string;
    inputs: {
      env: string;
      website: string;
      business_unit: string;
      country: string;
      feature: string;
      allure_tag: string;
    };
    report_url?: string | null;
    passed?: number;
    failed?: number;
    broken?: number;
    skipped?: number;
    unknown?: number;
  } | null;
  from_cache?: boolean; // Indicates if data came from cache (completed) or live API (active)
  testStats?: {
    passed: number;
    failed: number;
    broken: number;
    skipped: number;
    unknown: number;
    total: number;
    percentage: number;
  } | null;
  triggered_at?: string | null;
  passed?: number | null;
  failed?: number | null;
  broken?: number | null;
  skipped?: number | null;
  unknown?: number | null;
}

export const QaAutomatedTestsPage = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [result, setResult] = useState<TriggerResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [workflowRuns, setWorkflowRuns] = useState<WorkflowRun[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [showProdConfirm, setShowProdConfirm] = useState(false);
  const [reportStatsCache, setReportStatsCache] = useState<
    Map<
      number,
      {
        project: string;
        statistic: any;
        percentage: number;
        loading: boolean;
      }
    >
  >(new Map());
  const [backendHealthy, setBackendHealthy] = useState<boolean | null>(null);
  const [backendVersion, setBackendVersion] = useState<string>('');
  const [isGuestMode, setIsGuestMode] = useState<boolean>(false);
  
  // Filter state for Workflow Runs History
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [featureFilter, setFeatureFilter] = useState<string>('');
  const [tagsFilter, setTagsFilter] = useState<string>('');
  const [branchFilter, setBranchFilter] = useState<string>('');
  const [executedByFilter, setExecutedByFilter] = useState<string>('');
  const [triggerSourceFilter, setTriggerSourceFilter] = useState<string>('');
  const [availableBranches, setAvailableBranches] = useState<string[]>(['develop']);
  const [loadingBranches, setLoadingBranches] = useState(false);

  const [inputs, setInputs] = useState<WorkflowInputs>({
    env: 'staging',
    build_image: false,
    country: 'mx',
    business_unit: 'benandfrank',
    website: 'odoo',
    feature: '*',
    allure_tag: '',
    branch: 'develop',
  });

  const config = useApi(configApiRef);
  const identity = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  // Available tags based on website selection
  const availableTags: TagOption[] = getTagsForWebsite(inputs.website);

  // Available features based on BU, website, and env

  // Check backend health on mount
  useEffect(() => {
    const checkHealth = async () => {
      const health = await checkBackendHealth(discoveryApi, fetchApi);
      setBackendHealthy(health.healthy);
      if (health.version) setBackendVersion(health.version);
      if (!health.healthy) {
        // eslint-disable-next-line no-console
        console.warn('[QA Plugin] Backend health check failed:', health.error);
      }
    };
    checkHealth();
  }, [discoveryApi, fetchApi]);

  // Check if running in guest mode (development without GitHub auth)
  useEffect(() => {
    const checkGuestMode = async () => {
      try {
        const { token } = await identity.getCredentials();
        // If we have no token or empty token, we're in guest mode
        setIsGuestMode(!token);
      } catch {
        // If credentials fail, we're likely in guest mode
        setIsGuestMode(true);
      }
    };
    checkGuestMode();
  }, [identity]);

  // Fetch available branches when website changes
  useEffect(() => {
    const fetchBranches = async () => {
      setLoadingBranches(true);
      try {
        const backendUrl = config.getString('backend.baseUrl');
        const branchesUrl = `${backendUrl}/api/qa-automated-tests/branches/${inputs.website}`;
        
        const { token: backstageToken } = await identity.getCredentials();
        
        const response = await fetch(branchesUrl, {
          headers: {
            'Content-Type': 'application/json',
            ...(backstageToken ? { Authorization: `Bearer ${backstageToken}` } : {}),
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          setAvailableBranches(data.branches || ['develop']);
          
          // Reset to 'develop' if current branch not in list
          if (!data.branches?.includes(inputs.branch)) {
            setInputs(prev => ({ ...prev, branch: 'develop' }));
          }
        }
      } catch {
        setAvailableBranches(['develop']);
      } finally {
        setLoadingBranches(false);
      }
    };
    
    fetchBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs.website, config, identity]);

  // Fetch workflow runs history (uses GitHub App - works for all authenticated Backstage users)
  const fetchWorkflowRuns = async () => {
    setLoadingRuns(true);
    setRunsError(null);

    try {
      const backendUrl = config.getString('backend.baseUrl');
      const apiUrl = `${backendUrl}/api/qa-automated-tests/runs`;

      // Get Backstage auth token
      const { token: backstageToken } = await identity.getCredentials();

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${backstageToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.workflow_runs && Array.isArray(data.workflow_runs)) {
          setWorkflowRuns(data.workflow_runs);
        } else if (Array.isArray(data)) {
          setWorkflowRuns(data);
        } else {
          // eslint-disable-next-line no-console
          console.error('Unexpected response format:', data);
          setRunsError('Received unexpected data format from server');
          setWorkflowRuns([]);
        }
      } else {
        const errorData = await response.json();
        let errorMsg = 'Failed to fetch workflow runs';

        if (errorData.error) {
          errorMsg =
            typeof errorData.error === 'string'
              ? errorData.error
              : errorData.error.message || JSON.stringify(errorData.error);
        } else if (errorData.details) {
          errorMsg =
            typeof errorData.details === 'string'
              ? errorData.details
              : JSON.stringify(errorData.details);
        }

        setRunsError(errorMsg);
        setWorkflowRuns([]);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch workflow runs';
      setRunsError(errorMessage);
      setWorkflowRuns([]);
    } finally {
      setLoadingRuns(false);
    }
  };

  // Load workflow runs on mount
  useEffect(() => {
    fetchWorkflowRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch Allure stats for completed workflow runs
  useEffect(() => {
    if (workflowRuns.length === 0) return;

    const fetchStatsForRuns = async () => {
      for (const run of workflowRuns) {
        if (run.status === 'completed' && !reportStatsCache.has(run.id)) {
          const workflowInputs =
            run.cached_metadata?.inputs || (run as any).inputs;
          if (workflowInputs?.env && workflowInputs?.website) {
            const project = buildProjectName(workflowInputs);
            
            // Use specific report ID from workflow run URL if available
            const reportUrl = run.cached_metadata?.report_url || run.report_url;
            let reportId = 'latest';
            if (reportUrl) {
              const parsedReportId = parseReportIdFromUrl(reportUrl);
              if (parsedReportId) {
                reportId = parsedReportId;
              }
            }
            
            const stats = await fetchAllureReportSummary(
              discoveryApi,
              fetchApi,
              project,
              reportId,
            );
            if (stats) {
              setReportStatsCache(prev => {
                const newCache = new Map(prev);
                newCache.set(run.id, {
                  project,
                  statistic: stats.statistic,
                  percentage: stats.percentage,
                  loading: false,
                });
                return newCache;
              });
            }
          }
        }
      }
    };

    fetchStatsForRuns();
  }, [workflowRuns, discoveryApi, fetchApi, reportStatsCache]);

  const handleInputChange = (
    field: keyof WorkflowInputs,
    value: string | boolean,
  ) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const handleBuChange = (buId: string) => {
    const bu = getBusinessUnitById(buId);
    if (bu) {
      setInputs(prev => ({
        ...prev,
        country: bu.country,
        business_unit: bu.bu,
      }));
    }
  };

  const isFormValid = () => {
    if (!inputs.env || !inputs.website) {
      return false;
    }

    // Special validation for Odoo Production: only smokeprod/* feature allowed
    if (
      inputs.env === 'prod' &&
      inputs.website === 'odoo' &&
      inputs.feature !== PROD_ODOO_FEATURE
    ) {
      return false;
    }

    return true;
  };

  const getValidationMessage = (): string | null => {
    if (inputs.env === 'prod' && inputs.website === 'odoo') {
      if (inputs.feature !== PROD_ODOO_FEATURE) {
        return 'Production environment for Odoo only allows Smoke Tests (Production) feature module';
      }
    }

    return null;
  };

  const handleTrigger = async () => {
    if (inputs.env === 'prod' && !showProdConfirm) {
      setShowProdConfirm(true);
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setShowProdConfirm(false);

    try {
      const backendUrl = config.getString('backend.baseUrl');
      const apiUrl = `${backendUrl}/api/qa-automated-tests/run`;

      // Get Backstage auth token
      const { token: backstageToken } = await identity.getCredentials();

      // Calculate business unit ID from inputs
      const buId =
        BUSINESS_UNITS.find(
          bu => bu.country === inputs.country && bu.bu === inputs.business_unit,
        )?.id || '';

      // Get target URL from URL matrix (matching qa-self-service reference)
      const targetUrl = getUrl(inputs.website, inputs.env, buId);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${backstageToken}`,
        },
        body: JSON.stringify({
          ...inputs,
          url: targetUrl || '',
          build_image: inputs.build_image ? 'true' : 'false',
        }),
      });

      if (response.ok) {
        const data: TriggerResult = await response.json();
        setResult(data);
        setTimeout(() => fetchWorkflowRuns(), 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to trigger workflow');
      }
    } catch (err) {
      let errorMessage: string;
      if (err instanceof Error) {
        if (
          err.message.includes('Missing credentials') ||
          err.message.includes('Not signed in')
        ) {
          errorMessage =
            'Authentication required. Please sign in or contact your administrator.';
        } else {
          errorMessage = err.message;
        }
      } else {
        errorMessage = 'Failed to trigger workflow. Please try again.';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Extract unique values from workflow runs cache for filters
  const getUniqueFilterValues = (field: string): string[] => {
    const values = new Set<string>();
    
    workflowRuns.forEach(run => {
      if (field === 'status') {
        values.add(run.status);
      } else if (field === 'conclusion') {
        if (run.conclusion) values.add(run.conclusion);
      } else if (field === 'branch') {
        values.add(run.head_branch);
      } else if (field === 'executed_by') {
        const user = run.backstage_user || run.actor?.login;
        if (user) values.add(user);
      } else if (field === 'feature' || field === 'tags') {
        const workflowInputs = run.cached_metadata?.inputs || (run as any).inputs;
        if (workflowInputs) {
          if (field === 'feature') {
            values.add(workflowInputs.feature || '*');
          } else if (field === 'tags') {
            values.add(workflowInputs.allure_tag || 'no tags');
          }
        }
      } else if (field === 'trigger_source') {
        if (run.trigger_source) values.add(run.trigger_source);
      }
    });
    
    return Array.from(values).sort();
  };

  // Format executed_by for display
  const formatExecutedByFilter = (userRef: string): string => {
    if (!userRef) return 'Unknown';
    
    // Parse Backstage entity reference: "user:default/username" -> "username"
    if (userRef.includes('/')) {
      return userRef.split('/')[1] || userRef;
    }
    
    return userRef;
  };
  
  // Filter workflow runs based on selected filters
  const getFilteredWorkflowRuns = (): WorkflowRun[] => {
    return workflowRuns.filter(run => {
      // Status filter
      if (statusFilter && run.status !== statusFilter && run.conclusion !== statusFilter) {
        return false;
      }
      
      // Branch filter
      if (branchFilter && run.head_branch !== branchFilter) {
        return false;
      }
      
      // Executed By filter
      if (executedByFilter) {
        const runUser = run.backstage_user || run.actor?.login;
        if (formatExecutedByFilter(runUser) !== formatExecutedByFilter(executedByFilter)) {
          return false;
        }
      }
      
      // Feature and Tags filters (from cached_metadata)
      const workflowInputs = run.cached_metadata?.inputs || (run as any).inputs;
      
      if (featureFilter && workflowInputs?.feature !== featureFilter) {
        return false;
      }
      
      if (tagsFilter) {
        const runTag = workflowInputs?.allure_tag || 'no tags';
        if (runTag !== tagsFilter) {
          return false;
        }
      }

      // Trigger Source filter
      if (triggerSourceFilter && run.trigger_source !== triggerSourceFilter) {
        return false;
      }
      
      return true;
    });
  };

  const getHealthChipLabel = () => {
    if (backendHealthy === null) return 'Checking...';
    if (backendHealthy) return `Backend v${backendVersion}`;
    return 'Backend Unavailable';
  };

  const getHealthChipColor = () => {
    if (backendHealthy === null) return 'default';
    if (backendHealthy) return 'primary';
    return 'secondary';
  };

  const formatExecutedBy = (
    backstageUser: string | null | undefined,
    actorLogin: string,
  ): string => {
    if (!backstageUser) {
      return actorLogin;
    }

    // Parse Backstage entity reference: "user:default/username" -> "username"
    if (backstageUser.includes('/')) {
      return backstageUser.split('/')[1] || backstageUser;
    }

    return backstageUser;
  };

  const getTrafficLightStatus = (run: WorkflowRun) => {
    // Queued
    if (run.status === 'queued') {
      return (
        <Chip
          label="Queued"
          size="small"
          style={{
            backgroundColor: GITHUB_WORKFLOW_COLORS.queued,
            color: 'white',
          }}
        />
      );
    }

    // Running
    if (run.status === 'in_progress') {
      return (
        <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
          <Chip
            label="Running"
            size="small"
            style={{
              backgroundColor: GITHUB_WORKFLOW_COLORS.in_progress,
              color: 'white',
            }}
          />
          <CircularProgress size={16} />
        </Box>
      );
    }

    // Completed
    if (run.status === 'completed') {
      // Cancelled
      if (run.conclusion === 'cancelled') {
        return (
          <Chip
            label="Cancelled"
            size="small"
            style={{
              backgroundColor: GITHUB_WORKFLOW_COLORS.cancelled,
              color: 'white',
            }}
          />
        );
      }

      // Skipped
      if (run.conclusion === 'skipped') {
        return (
          <Chip
            label="Skipped"
            size="small"
            style={{
              backgroundColor: GITHUB_WORKFLOW_COLORS.skipped,
              color: 'white',
            }}
          />
        );
      }

      // Success
      if (run.conclusion === 'success') {
        return (
          <Chip
            label="Success"
            size="small"
            style={{
              backgroundColor: GITHUB_WORKFLOW_COLORS.success,
              color: 'white',
            }}
          />
        );
      }

      // Failure
      if (run.conclusion === 'failure') {
        return (
          <Chip
            label="Failed"
            size="small"
            style={{
              backgroundColor: GITHUB_WORKFLOW_COLORS.failure,
              color: 'white',
            }}
          />
        );
      }
    }

    // Unknown status
    return (
      <Chip
        label="Unknown"
        size="small"
        style={{
          backgroundColor: GITHUB_WORKFLOW_COLORS.cancelled,
          color: 'white',
        }}
      />
    );
  };

  const currentBuId =
    BUSINESS_UNITS.find(
      bu => bu.country === inputs.country && bu.bu === inputs.business_unit,
    )?.id || '';

  const availableFeatures: FeatureModule[] = getAvailableFeatures(
    inputs.website,
    currentBuId,
    inputs.env,
  );

  return (
    <Page themeId="tool">
      <Header
        title="QA Automated Tests"
        subtitle="Trigger automated test workflows"
      >
        <Button
          variant="outlined"
          color="primary"
          startIcon={<CloudDownloadIcon />}
          onClick={() => navigate('/qa-automated-tests/s3-reports')}
          style={{ marginRight: 8 }}
        >
          Browse S3 Reports
        </Button>
      </Header>
      <Content>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          padding={4}
        >
          <Box width="100%">
            <InfoCard title="Workflow Trigger">
              <Box padding={2}>
                <Box marginBottom={2}>
                  <Chip
                    label={getHealthChipLabel()}
                    color={getHealthChipColor()}
                    size="small"
                  />
                </Box>
                {isGuestMode && (
                  <Box marginTop={2} marginBottom={2}>
                    <Alert severity="info">
                      <Typography variant="body2">
                        <strong>Development Mode</strong>
                        <br />
                        Running in guest mode. Workflows will be triggered as "guest" user.
                      </Typography>
                    </Alert>
                  </Box>
                )}
                <Typography variant="body1" gutterBottom>
                  Trigger the QA automation CI workflow in the galileo-pipelines
                  repository.
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Repository: benandfrank/galileo-pipelines | Workflow:
                  backstage-automated-tests.yaml
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ marginTop: 8, fontStyle: 'italic' }}
                >
                  Workflows run with Galileo IDP GitHub App credentials.
                </Typography>

                {/* Target URL Preview (qa-self-service style) */}
                {(() => {
                  const buId =
                    BUSINESS_UNITS.find(
                      bu =>
                        bu.country === inputs.country &&
                        bu.bu === inputs.business_unit,
                    )?.id || '';
                  const targetUrl = getUrl(inputs.website, inputs.env, buId);
                  return (
                    <Box marginTop={2} marginBottom={2}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Target URL:</strong>{' '}
                        {targetUrl ? (
                          <Link
                            href={targetUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontFamily: 'monospace' }}
                          >
                            {targetUrl}
                          </Link>
                        ) : (
                          <span style={{ fontStyle: 'italic' }}>
                            Select a Business Unit to complete the URL
                          </span>
                        )}
                      </Typography>
                    </Box>
                  );
                })()}

                {inputs.env === 'prod' && inputs.website === 'odoo' && (
                  <Box marginTop={2} marginBottom={2}>
                    <Alert severity="info">
                      <Typography variant="body2">
                        <strong>Production Odoo</strong>
                        <br />
                        Only the Smoke Tests (Production) feature module is allowed in this environment.
                      </Typography>
                    </Alert>
                  </Box>
                )}

                <Box marginTop={3}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        label="Environment *"
                        value={inputs.env}
                        onChange={e =>
                          handleInputChange(
                            'env',
                            e.target.value as Environment,
                          )
                        }
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        required
                        error={!inputs.env}
                        helperText={
                          !inputs.env ? 'Environment is required' : ''
                        }
                      >
                        {Object.entries(ENVIRONMENTS).map(([value, label]) => (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        label="Business Unit & Country *"
                        value={currentBuId}
                        onChange={e => handleBuChange(e.target.value)}
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        required
                        helperText="Select business unit and country"
                      >
                        {BUSINESS_UNITS.map(bu => (
                          <MenuItem key={bu.id} value={bu.id}>
                            {bu.icon} {bu.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        label="Application *"
                        value={inputs.website}
                        onChange={e =>
                          handleInputChange(
                            'website',
                            e.target.value as Website,
                          )
                        }
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        required
                        error={!inputs.website}
                        helperText={
                          !inputs.website
                            ? 'Application is required'
                            : 'Select target application'
                        }
                      >
                        {Object.entries(WEBSITES).map(([value, label]) => (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        label="Test Suite Tag"
                        value={inputs.allure_tag}
                        onChange={e =>
                          handleInputChange('allure_tag', e.target.value)
                        }
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        helperText={
                          availableTags.length > 0
                            ? `Tags for ${WEBSITES[inputs.website]}`
                            : 'Select an application first'
                        }
                      >
                        <MenuItem value="">None - Run All Features</MenuItem>
                        {availableTags.map(tag => (
                          <MenuItem key={tag.v} value={tag.v}>
                            {tag.l}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        label="Feature Module"
                        value={inputs.feature}
                        onChange={e =>
                          handleInputChange('feature', e.target.value)
                        }
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        helperText={
                          availableFeatures.length > 0
                            ? `${availableFeatures.reduce(
                                (sum, f) => sum + f.c,
                                0,
                              )} test cases available`
                            : 'Select BU and application first'
                        }
                      >
                        <MenuItem value="*">
                          All Features (
                          {availableFeatures.reduce((sum, f) => sum + f.c, 0)}{' '}
                          cases)
                        </MenuItem>
                        {availableFeatures.map(feature => (
                          <MenuItem
                            key={feature.f}
                            value={getFeatureValue(feature.f)}
                          >
                            {feature.n} ({feature.c} cases)
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        label="Branch"
                        value={inputs.branch}
                        onChange={e =>
                          handleInputChange('branch', e.target.value)
                        }
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        disabled={loadingBranches}
                        helperText={
                          loadingBranches
                            ? 'Loading branches...'
                            : `From ${
                                inputs.website === 'odoo'
                                  ? 'galileo-qa-odoo'
                                  : 'galileo-qa-ecommerce'
                              } repo`
                        }
                      >
                        {availableBranches.map(branch => (
                          <MenuItem key={branch} value={branch}>
                            {branch}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                </Box>

                {getValidationMessage() && (
                  <Box marginTop={2}>
                    <Alert severity="error">{getValidationMessage()}</Alert>
                  </Box>
                )}

                <Box marginTop={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleTrigger}
                    disabled={loading || !isFormValid()}
                    size="large"
                  >
                    {(() => {
                      if (loading) return 'Triggering...';

                      return 'Run Tests';
                    })()}
                  </Button>
                </Box>

                {loading && <Progress />}

                {error && (
                  <Box marginTop={2}>
                    <Alert severity="error">{error}</Alert>
                  </Box>
                )}

                {result && (
                  <Box marginTop={2}>
                    <Alert severity="success">
                      <Typography variant="h6">
                        Workflow triggered successfully!
                      </Typography>
                      {result.inputs && (
                        <Box marginTop={1}>
                          <Typography
                            variant="body2"
                            style={{
                              fontFamily: 'monospace',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {JSON.stringify(result.inputs, null, 2)}
                          </Typography>
                        </Box>
                      )}
                      <Typography variant="body2" style={{ marginTop: '8px' }}>
                        <a
                          href={`https://github.com/${result.repository}/actions`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'inherit' }}
                        >
                          View workflow runs
                        </a>
                      </Typography>
                    </Alert>
                  </Box>
                )}
              </Box>
            </InfoCard>
          </Box>

          <Dialog
            open={showProdConfirm}
            onClose={() => setShowProdConfirm(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              Confirm Production Test Run
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                You are about to trigger tests in the{' '}
                <strong>PRODUCTION</strong> environment.
                <br />
                <br />
                Configuration:
                <br />
                Application: {WEBSITES[inputs.website]}
                <br />
                Business Unit:{' '}
                {
                  BUSINESS_UNITS.find(
                    bu =>
                      bu.country === inputs.country &&
                      bu.bu === inputs.business_unit,
                  )?.label
                }
                <br />
                Feature: {inputs.feature}
                {inputs.allure_tag && (
                  <>
                    <br />
                    Test Tag:{' '}
                    {availableTags.find(t => t.v === inputs.allure_tag)?.l ||
                      inputs.allure_tag}
                  </>
                )}
                <br />
                <br />
                <strong>Are you sure you want to proceed?</strong>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowProdConfirm(false)} color="primary">
                Cancel
              </Button>
              <Button
                onClick={handleTrigger}
                color="secondary"
                variant="contained"
              >
                Yes, Run Production Tests
              </Button>
            </DialogActions>
          </Dialog>

          <Box width="100%" marginTop={4}>
            <InfoCard title="Workflow Runs History">
              <Box padding={2}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  marginBottom={2}
                >
                  <Typography variant="body2" color="textSecondary">
                    Last 20 workflow runs for backstage-automated-tests.yaml
                    <br />
                    <span style={{ fontStyle: 'italic', fontSize: '0.9em' }}>
                      Uses GitHub App authentication (works for all Backstage
                      users)
                    </span>
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={fetchWorkflowRuns}
                    disabled={loadingRuns}
                  >
                    {loadingRuns ? 'Refreshing...' : 'Refresh'}
                  </Button>
                </Box>

                {loadingRuns && <Progress />}

                {runsError && (
                  <Alert severity="warning" style={{ marginTop: 16 }}>
                    {runsError}
                  </Alert>
                )}

                {!loadingRuns && !runsError && workflowRuns.length > 0 && (
                  <Box marginBottom={2}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      <strong>Filters:</strong> (values from cache)
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          select
                          label="Status"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        >
                          <MenuItem value="">All</MenuItem>
                          {getUniqueFilterValues('status').map(status => (
                            <MenuItem key={status} value={status}>
                              {status}
                            </MenuItem>
                          ))}
                          {getUniqueFilterValues('conclusion').map(conclusion => (
                            <MenuItem key={conclusion} value={conclusion}>
                              {conclusion}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          select
                          label="Feature"
                          value={featureFilter}
                          onChange={(e) => setFeatureFilter(e.target.value)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        >
                          <MenuItem value="">All</MenuItem>
                          {getUniqueFilterValues('feature').map(feature => (
                            <MenuItem key={feature} value={feature}>
                              {feature === '*' ? 'All Features' : feature}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          select
                          label="Tags"
                          value={tagsFilter}
                          onChange={(e) => setTagsFilter(e.target.value)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        >
                          <MenuItem value="">All</MenuItem>
                          {getUniqueFilterValues('tags').map(tag => (
                            <MenuItem key={tag} value={tag}>
                              {tag || 'no tags'}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          select
                          label="Branch"
                          value={branchFilter}
                          onChange={(e) => setBranchFilter(e.target.value)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        >
                          <MenuItem value="">All</MenuItem>
                          {getUniqueFilterValues('branch').map(branch => (
                            <MenuItem key={branch} value={branch}>
                              {branch}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          select
                          label="Executed By"
                          value={executedByFilter}
                          onChange={(e) => setExecutedByFilter(e.target.value)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        >
                          <MenuItem value="">All</MenuItem>
                          {getUniqueFilterValues('executed_by').map(user => (
                            <MenuItem key={user} value={user}>
                              {formatExecutedByFilter(user)}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>


                       <Grid item xs={12} sm={6} md={2}>
                         <TextField
                           select
                           label="Trigger Source"
                           value={triggerSourceFilter}
                           onChange={(e) => setTriggerSourceFilter(e.target.value)}
                           variant="outlined"
                           size="small"
                           fullWidth
                           InputLabelProps={{ shrink: true }}
                         >
                           <MenuItem value="">All</MenuItem>
                           {getUniqueFilterValues('trigger_source').map(source => (
                             <MenuItem key={source} value={source}>
                               {source}
                             </MenuItem>
                           ))}
                         </TextField>
                       </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setStatusFilter('');
                            setFeatureFilter('');
                            setTagsFilter('');
                            setBranchFilter('');
                            setExecutedByFilter('');
                             setTriggerSourceFilter('');
                          }}
                          disabled={!statusFilter && !featureFilter && !tagsFilter && !branchFilter && !executedByFilter}
                        >
                          Clear Filters
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {!loadingRuns && !runsError && getFilteredWorkflowRuns().length === 0 && workflowRuns.length > 0 && (
                  <Typography variant="body2" color="textSecondary">
                    No workflow runs match the selected filters
                  </Typography>
                )}

                {!loadingRuns && !runsError && workflowRuns.length === 0 && (
                  <Typography variant="body2" color="textSecondary">
                    No workflow runs found
                  </Typography>
                )}

                {!loadingRuns && workflowRuns.length > 0 && getFilteredWorkflowRuns().length > 0 && (
                  <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8, display: 'block' }}>
                    Showing {getFilteredWorkflowRuns().length} of {workflowRuns.length} workflow runs
                  </Typography>
                )}
                
                {!loadingRuns && workflowRuns.length > 0 && getFilteredWorkflowRuns().length > 0 && (
                  <Table
                    options={{
                      padding: 'dense',
                      search: false,
                      pageSize: 10,
                    }}
                    data={getFilteredWorkflowRuns().map(run => {
                      // Calculate testStats from run data
                      let testStats = null;
                      if (run.cached_metadata?.passed !== undefined || run.passed !== undefined) {
                        const passed = run.cached_metadata?.passed ?? run.passed ?? 0;
                        const failed = run.cached_metadata?.failed ?? run.failed ?? 0;
                        const broken = run.cached_metadata?.broken ?? run.broken ?? 0;
                        const skipped = run.cached_metadata?.skipped ?? run.skipped ?? 0;
                        const unknown = run.cached_metadata?.unknown ?? run.unknown ?? 0;
                        const total = passed + failed + broken + skipped + unknown;
                        
                        testStats = {
                          passed,
                          failed,
                          broken,
                          skipped,
                          unknown,
                          total,
                          percentage: total > 0 ? (passed / total) * 100 : 0,
                        };
                      }
                      
                      return {
                        id: run.id,
                        run_number: run.run_number,
                        status: run.status,
                        conclusion: run.conclusion,
                        event: run.event,
                        branch: run.head_branch,
                        actor: run.actor.login,
                        backstage_user: run.backstage_user,
                        cached_metadata: run.cached_metadata,
                        created: run.created_at,
                        updated: run.updated_at,
                        url: run.html_url,
                        trigger_source: run.trigger_source,
                        testStats,
                        triggered_at: run.cached_metadata?.triggered_at || run.created_at,
                      };
                    })}
                    columns={[
                      {
                        title: 'Run ID',
                        field: 'id',
                        render: (row: any) => (
                          <Link
                            href={row.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            #{row.id}
                          </Link>
                        ),
                      },
                      {
                        title: 'Status',
                        field: 'status',
                        render: (row: any) => getTrafficLightStatus(row),
                      },
                      {
                        title: 'Test Results',
                        field: 'testStats',
                        render: (row: any) => {
                          if (!row.testStats) {
                            return (
                              <Typography variant="caption" color="textSecondary">
                                —
                              </Typography>
                            );
                          }
                          
                          return (
                            <Box display="flex" alignItems="center" style={{ gap: '4px', flexWrap: 'wrap' }}>
                              <Chip 
                                label={`${row.testStats.passed} passed`}
                                size="small"
                                style={{ backgroundColor: '#28a745', color: 'white', fontSize: '0.7rem' }}
                              />
                              {row.testStats.failed > 0 && (
                                <Chip 
                                  label={`${row.testStats.failed} failed`}
                                  size="small"
                                  style={{ backgroundColor: '#cb2431', color: 'white', fontSize: '0.7rem' }}
                                />
                              )}
                              {row.testStats.broken > 0 && (
                                <Chip 
                                  label={`${row.testStats.broken} broken`}
                                  size="small"
                                  style={{ backgroundColor: '#ffc107', color: 'black', fontSize: '0.7rem' }}
                                />
                              )}
                              <Typography variant="caption" color="textSecondary" style={{ marginLeft: 4 }}>
                                ({row.testStats.percentage.toFixed(0)}% pass rate)
                              </Typography>
                            </Box>
                          );
                        },
                      },
                      {
                        title: 'Trigger Source',
                        field: 'trigger_source',
                        render: (row: any) => {
                          if (!row.trigger_source) {
                            return (
                              <Typography variant="body2" color="textSecondary">
                                -
                              </Typography>
                            );
                          }
                          return (
                            <Chip
                              label={row.trigger_source}
                              size="small"
                              variant="outlined"
                            />
                          );
                        },
                      },
                      {
                        title: 'Allure Report',
                        field: 'id',
                        render: (row: any) => {
                          if (row.status !== 'completed') {
                            return (
                              <Typography variant="body2" color="textSecondary">
                                -
                              </Typography>
                            );
                          }

                          const workflowInputs =
                            row.cached_metadata?.inputs || row.inputs;
                          if (
                            !workflowInputs?.env ||
                            !workflowInputs?.website
                          ) {
                            return (
                              <Typography variant="body2" color="textSecondary">
                                -
                              </Typography>
                            );
                          }

                          const cachedStats = reportStatsCache.get(row.id);

                          return (
                            <AllureReportLink
                              runId={row.id}
                              status={row.status}
                              workflowInputs={workflowInputs}
                              discoveryApi={discoveryApi}
                              fetchApi={fetchApi}
                              cachedMetadata={row.cached_metadata}
                              cachedStats={cachedStats}
                            />
                          );
                        },
                      },
                      {
                        title: 'Feature',
                        field: 'id',
                        render: (row: any) => {
                          const workflowInputs =
                            row.cached_metadata?.inputs || row.inputs;
                          if (!workflowInputs?.feature) {
                            return (
                              <Typography variant="body2" color="textSecondary">
                                -
                              </Typography>
                            );
                          }
                          return (
                            <Chip
                              label={
                                workflowInputs.feature === '*'
                                  ? 'All Features'
                                  : workflowInputs.feature
                              }
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          );
                        },
                      },
                      {
                        title: 'TAGS',
                        field: 'id',
                        render: (row: any) => {
                          const workflowInputs =
                            row.cached_metadata?.inputs || row.inputs;
                          const allureTag = workflowInputs?.allure_tag;
                          
                          // Show "no tags" chip if allure_tag is empty or missing
                          return (
                            <Chip
                              label={allureTag || 'no tags'}
                              size="small"
                              variant="outlined"
                              color={allureTag ? "secondary" : "default"}
                            />
                          );
                        },
                      },
                      {
                        title: 'Branch',
                        field: 'branch',
                        render: (row: any) => (
                          <Typography
                            variant="body2"
                            style={{ fontFamily: 'monospace' }}
                          >
                            {row.branch}
                          </Typography>
                        ),
                      },
                      {
                        title: 'Executed By',
                        field: 'actor',
                        render: (row: any) => (
                          <Box display="flex" alignItems="center">
                            <Typography variant="body2">
                              {formatExecutedBy(
                                row.backstage_user,
                                row.actor?.login || 'Unknown',
                              )}
                            </Typography>
                            {row.from_cache === false && (
                              <Chip
                                label="live"
                                size="small"
                                style={{
                                  marginLeft: 8,
                                  fontSize: '0.65em',
                                  height: '16px',
                                }}
                              />
                            )}
                          </Box>
                        ),
                      },
                      {
                        title: 'Created',
                        field: 'created',
                        render: (row: any) => formatDate(row.created),
                      },
                    ]}
                  />
                )}
              </Box>
            </InfoCard>
          </Box>
        </Box>
      </Content>
    </Page>
  );
};
